/**
 * Murphy Door Configurator - Material Remap Coordinator
 *
 * PURPOSE: apo_style only. Handles the moment the customer changes wood/finish
 * AFTER they have already styled the door: carries every styling component
 * across to the new material, keeping the product they chose, and leaves
 * measurements untouched. Warns only when something cannot come across.
 *
 * WHY THIS EXISTS. Before this module, a late wood change did three different
 * things depending on how many compatible variants a category had left
 * (md-config-bl-material-preference-coordinator.js, computeAndPublishFilterState):
 *
 *   exactly 1 compatible  -> auto-applied (SELECTION_AUTO_APPLY_REQUEST)
 *   0 compatible          -> STATE_CATEGORY_COMPLETED / 'material_filtered',
 *                            i.e. the card was quietly marked DONE even though
 *                            the customer's choice no longer existed
 *   2+ compatible         -> tiles re-filtered and re-sorted, but the applied
 *                            selection was never re-pointed, so the door kept
 *                            rendering the old material
 *
 * The 2+ case is the common one, and it is the visible bug: pick Red Oak,
 * configure everything, switch to Knotty Alder, and the cabinet doors stay Red
 * Oak. The 0 case is the dangerous one, because it reaches the cart looking
 * complete.
 *
 * IT REMAPS. Each affected component keeps the product the customer chose and
 * only the material underneath it moves. Measurements never move: planRemap
 * skips material-agnostic products, so Width/Height are structurally incapable
 * of being remapped rather than special-cased out.
 *
 * A warning is raised ONLY when something genuinely cannot come across -- the
 * customer's product has no variant in the new material. In that case nothing
 * is applied at all; a half-migrated door the customer never asked for is
 * worse than a clear question.
 *
 * HISTORY, because the previous version of this comment was wrong and cost a
 * day. Between 2026-08-17 and 2026-08-18 this module warned-and-reset instead
 * of remapping. The stated reason was that the hot-swap "works in an automated
 * browser session but could not be made to work in Daniel's browser", blamed on
 * an unidentified environmental difference. There was no environmental
 * difference. The automated verification drove the configurator with synthetic
 * pointer events, which do NOT trigger the conductor's click-apply path even
 * when click_apply is enabled -- so it was confirming behaviour against applied
 * state a real customer never produces. The remap was never actually shown to
 * fail. Re-enabled 2026-08-18 after Ryan flagged that his own build carried
 * styles across a wood swap.
 *
 * The lesson generalises: this configurator cannot be verified by dispatching
 * synthetic clicks. Drive it by hand, or the result describes a state that
 * does not exist.
 *
 * PHASE: 2 (Event-Driven CCP - Strict Enforcement)
 * MODULE TYPE: Business Logic (bl-)
 * GOLDEN STANDARD REFERENCE: md-config-bl-shelf-mirror-coordinator.js
 *
 * CCP COMPLIANCE:
 * - Event-driven only; no direct BL/UI module calls
 * - PPD-first data access via conductor delegation
 *   (getAppliedSelectionsMap, getVirtualizedProductsForCategory,
 *   getMaterialPreferences)
 * - Reuses the EXISTING user-unapply flow by publishing
 *   selection:user_unapply_requested per affected category -- the same event a
 *   card reset fires, so the conductor still clears state, re-persists the
 *   ?config= URL and recalculates price. Composition, not duplication.
 * - Bootstrap barrier pattern for race protection (Golden Pattern #9)
 *
 * STATE MONITORING IS REUSED, NOT REINVENTED. Daniel asked for "a variable
 * called link_state ... set to the currently updated url ... updated each time
 * we update our selections". That already exists: state-manager.persistState()
 * runs on every apply/unapply and calls persistence-manager.updateURLState(),
 * so window.location.href is ALWAYS the serialized current configuration. This
 * module only keeps a short rolling history of those URLs so it can hand back
 * the one from BEFORE the material change. That history is also the "linked
 * list or queue of changes" Daniel wanted for a future undo button.
 */
(function (window) {
  'use strict';

  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;

  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  const LOG_PREFIX = '[MaterialRemapCoordinator]';

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION
  // ============================================================================
  let bootstrapComplete = false;
  let pendingCalls = [];

  // PATTERN 15: CLEANUP ARRAY
  let cleanupHandlers = [];

  /**
   * Rolling history of serialized-configuration URLs, oldest last.
   * history[0] is the CURRENT configuration, history[1] the one before it.
   * Bounded so a long session can't grow it without limit.
   */
  const urlHistory = [];
  const MAX_URL_HISTORY = 25;

  /**
   * The URL to return to if the customer declines the reset. Captured at the
   * moment a material change is detected, before any of this module's own
   * applies have run.
   */
  let pendingRevertUrl = null;

  /**
   * Categories the pending wood change will reset, captured when the warning
   * is raised so the confirm handler clears exactly those and nothing else.
   * Measurements and other material-agnostic categories are never in here --
   * planRemap only ever returns material-aware components -- which is how
   * Width/Height survive the reset.
   */
  let pendingResetCategories = [];

  /**
   * The material the customer is switching TO, held while the conflict warning
   * is on screen so the reset handler can re-plan against it.
   *
   * Deliberately the wood/finish pair and NOT the plan itself. The plan is
   * computed when the modal opens but acted on when the customer answers, and
   * they can sit on it: replaying variant ids captured minutes earlier could
   * overwrite a selection they changed in between, which is the same silent
   * wrong answer findVariantForMaterial's exact-match rule exists to prevent.
   * Re-planning costs one pass over the applied map and is always current.
   */
  let pendingMaterial = null;

  /**
   * DEBOUNCE + RE-ENTRANCY GUARD. Both are required, for different reasons,
   * and the module is unusable without either. Measured live 2026-08-17: one
   * wood swap published MATERIAL_PREFERENCE_APPLIED **12 times**.
   *
   * 1. The material arrives in PIECES. Applying the wood card's variant emits
   *    a materialType:'wood' event first, carrying the NEW wood but still the
   *    OLD finish (WN + MS), and only the following materialType:'finish'
   *    event completes the picture (WN + UN). Acting on the first event
   *    remaps every component to a material the customer never chose. So we
   *    wait for the burst to settle and use the final pair.
   *
   * 2. Our own remap FEEDS BACK. Applying a component re-asserts the material
   *    preference derived from that component's variant, which publishes
   *    another MATERIAL_PREFERENCE_APPLIED, which would trigger another
   *    remap. Live this ping-ponged the finish UN -> MS -> UN indefinitely.
   *    So the module ignores events while its own apply is in flight.
   */
  let settleTimer = null;
  let latestMaterial = null;
  let remapInFlight = false;
  const SETTLE_MS = 200;
  // Safety valve: if the cascade never reports completion the flag would latch
  // on and silently disable remapping for the rest of the session.
  const IN_FLIGHT_MAX_MS = 8000;
  let inFlightTimer = null;
  // Layout-settle delay before asking navigation-manager to scroll to a
  // re-asked gate. Deliberately the same 400ms the ATC scroll uses in
  // md-config-ui-updater.js -- one settle number for this codebase, not two.
  const GATE_SCROLL_SETTLE_MS = 400;
  // Only ever ONE pending gate scroll. Two resets in quick succession would
  // otherwise queue two, and the second would yank the customer away from a
  // gate they had already started answering.
  let gateScrollTimer = null;

  function clearInFlight() {
    remapInFlight = false;
    if (inFlightTimer) {
      clearTimeout(inFlightTimer);
      inFlightTimer = null;
    }
  }

  function isApoStyle() {
    const conductor = window.MurphyDoorConfigurator.conductor;
    return conductor?._getConfiguratorExperience?.() === 'apo_style';
  }

  /**
   * Records the current URL. Called after selections settle, so the entry is
   * whatever persistence-manager.updateURLState() last wrote.
   */
  function recordUrl() {
    try {
      const href = window.location.href;
      if (urlHistory[0] === href) return; // no change, don't pad the history
      urlHistory.unshift(href);
      if (urlHistory.length > MAX_URL_HISTORY)
        urlHistory.length = MAX_URL_HISTORY;
    } catch (error) {
      isDev() && console.error(LOG_PREFIX + ' recordUrl failed:', error);
    }
  }

  /**
   * True when a product participates in the material system at all. A product
   * whose variants carry no wood_sku (Delivery, Rush, and other flat add-ons)
   * is material-agnostic: a wood change neither can nor should move it, and it
   * must NEVER be reported as "unavailable in the new stain" -- doing so would
   * pop the reset warning on configurations that are perfectly fine.
   * @param {Object} product
   * @returns {boolean}
   */
  function isMaterialAware(product) {
    return (product?.variants || []).some((v) => v && v.wood_sku);
  }

  /**
   * Finds the variant of THIS product carrying the target wood + finish.
   *
   * Matching is on wood_sku/finish_sku (stamped onto every variant by
   * md-config-support-variant-enhancer.js), never on variant title. Titles are
   * display strings and are not guaranteed to be stable or unique -- the same
   * trap that made the shelf mirror apply the wrong shelf type.
   *
   * @param {Object} product
   * @param {string|null} woodSku
   * @param {string|null} finishSku
   * @returns {Object|null} the matching variant
   */
  function findVariantForMaterial(product, woodSku, finishSku) {
    const variants = product?.variants || [];
    if (!woodSku) return null;

    // Exact wood + finish is the only fully-faithful remap.
    const exact = variants.find(
      (v) => v && v.wood_sku === woodSku && v.finish_sku === finishSku
    );
    if (exact) return exact;

    // Wood matches but this product doesn't offer that finish. Deliberately
    // treated as UNMAPPABLE rather than silently substituting another finish:
    // quietly changing the customer's stain is exactly the class of silent
    // wrong answer this module exists to stop. Returning null routes it into
    // the warning instead.
    return null;
  }

  /**
   * Walks every applied selection and sorts it into remappable vs unmappable
   * for the target material.
   * @param {Object} conductor
   * @param {string|null} woodSku
   * @param {string|null} finishSku
   * @returns {{remaps: Array, unmappable: Array}}
   */
  function planRemap(conductor, woodSku, finishSku) {
    const remaps = [];
    const unmappable = [];

    const appliedMap = conductor.getAppliedSelectionsMap
      ? conductor.getAppliedSelectionsMap()
      : null;
    if (!appliedMap || typeof appliedMap.forEach !== 'function') {
      return { remaps, unmappable };
    }

    const materialSourceCategories = CONSTANTS.MATERIAL_SOURCE_CATEGORIES || [];

    appliedMap.forEach((applied, categoryId) => {
      if (!applied || applied.variantId == null) return;

      // The card that CHOOSES the wood is never remapped or warned about --
      // its products are the woods themselves, so the outgoing one never has
      // a variant in the incoming material and would be reported unmappable
      // on every single swap. See MATERIAL_SOURCE_CATEGORIES.
      if (materialSourceCategories.indexOf(categoryId) !== -1) return;

      const products =
        conductor.getVirtualizedProductsForCategory(categoryId) || [];

      // Locate the product the applied variant belongs to, so the remap keeps
      // the customer's actual CHOICE (e.g. "Upper Shaker Cabinet Door") and
      // only swaps the material underneath it.
      let appliedProduct = null;
      let appliedVariant = null;
      for (const product of products) {
        const match = (product.variants || []).find(
          (v) => v && String(v.id) === String(applied.variantId)
        );
        if (match) {
          appliedProduct = product;
          appliedVariant = match;
          break;
        }
      }
      if (!appliedProduct || !appliedVariant) return;

      // Material-agnostic products are untouched by a wood change.
      if (!isMaterialAware(appliedProduct)) return;

      // Already in the target material -- nothing to do. This is also what
      // skips the Wood & Finish card itself, without hardcoding its name.
      if (
        appliedVariant.wood_sku === woodSku &&
        appliedVariant.finish_sku === finishSku
      ) {
        return;
      }

      const target = findVariantForMaterial(appliedProduct, woodSku, finishSku);
      if (target) {
        remaps.push({
          categoryId,
          productId: appliedProduct.id,
          variantId: target.id,
        });
      } else {
        unmappable.push({
          categoryId,
          productTitle: appliedProduct.title || categoryId,
        });
      }
    });

    return { remaps, unmappable };
  }

  /**
   * Handles a material preference change. apo_style only.
   * @param {Object} eventData - { materialType, materialSku, preferences, ... }
   */
  function handleMaterialPreferenceApplied(eventData) {
    if (!bootstrapComplete) {
      pendingCalls.push({
        fn: handleMaterialPreferenceApplied,
        args: [eventData],
      });
      return;
    }
    if (!isApoStyle()) return;

    // Ignore the echo of our own applies -- see the guard block above.
    if (remapInFlight) return;

    const prefs0 = eventData?.preferences || {};
    const w = prefs0.appliedWoodSku || prefs0.selectedWood || null;
    if (!w) return;
    latestMaterial = {
      woodSku: w,
      finishSku: prefs0.appliedFinishSku || prefs0.selectedFinish || null,
    };

    // Wait for the wood/finish burst to settle, then act once on the final
    // pair. Without this the materialType:'wood' event fires first carrying
    // the OLD finish and every component gets remapped to a material the
    // customer never picked.
    if (settleTimer) clearTimeout(settleTimer);
    settleTimer = setTimeout(() => {
      settleTimer = null;
      const m = latestMaterial;
      latestMaterial = null;
      if (m) runRemap(m.woodSku, m.finishSku);
    }, SETTLE_MS);
  }

  /**
   * Does the actual remap for a settled wood/finish pair.
   * @param {string|null} woodSku
   * @param {string|null} finishSku
   */
  function runRemap(woodSku, finishSku) {
    const conductor = window.MurphyDoorConfigurator.conductor;
    if (!conductor || !conductor.getAppliedSelectionsMap) return;

    if (!woodSku) return; // nothing to remap toward

    // Capture the pre-change URL BEFORE applying anything. urlHistory[0] is
    // the state as of the material change itself; [1] is the configuration the
    // customer had before it, which is what "stay on this one" restores.
    const revertUrl = urlHistory[1] || urlHistory[0] || null;

    let plan;
    try {
      plan = planRemap(conductor, woodSku, finishSku);
    } catch (error) {
      isDev() && console.error(LOG_PREFIX + ' planRemap failed:', error);
      return;
    }

    // REMAP, not reset. The customer's styling carries across to the new
    // material: each component keeps the product they chose (e.g. "Upper
    // Shaker Cabinet Door") and only the material underneath it moves.
    // Measurements never move, because planRemap skips material-agnostic
    // products entirely -- that is a property of the planner, not a special
    // case here.
    if (plan.unmappable.length > 0) {
      // Do NOT apply the partial remap first. Half-migrating the configuration
      // and then asking whether to reset would leave the customer looking at a
      // door that is neither what they had nor what they asked for while they
      // decide.
      pendingRevertUrl = revertUrl;
      pendingResetCategories = plan.unmappable.map((u) => u.categoryId);

      // Hold the target material so the reset handler can re-plan. Without
      // this, plan.remaps was computed here and then DISCARDED by the return
      // below, so a single unmappable component stranded every remappable one
      // on the old material: never moved, and never in the reset list either.
      // Measured live 2026-08-25 on flush-mount-bookcase-door-2-0 -- two
      // unmappable cabinet doors left Front Casing, Top Section and Bottom
      // Section sitting on Knotty Alder/Clear Stain under a TFL door.
      pendingMaterial = { woodSku, finishSku };

      // Suppress follow-on events until the customer answers. One wood change
      // emits a burst of MATERIAL_PREFERENCE_APPLIED (12 measured live); the
      // debounce collapses a tight burst, but a straggler would stack a second
      // modal on the first. Both answer handlers clear this.
      remapInFlight = true;
      if (inFlightTimer) clearTimeout(inFlightTimer);
      inFlightTimer = setTimeout(clearInFlight, IN_FLIGHT_MAX_MS);

      isDev() &&
        console.warn(
          LOG_PREFIX +
            ' ' +
            plan.unmappable.length +
            ' selection(s) unavailable in ' +
            woodSku +
            '/' +
            finishSku +
            ' -- warning instead of half-applying',
          plan.unmappable
        );

      publish(CONSTANTS.EVENTS.UI_MATERIAL_REMAP_CONFLICT, {
        woodSku,
        finishSku,
        unmappable: plan.unmappable,
        affected: plan.unmappable.map((u) => u.categoryId),
        revertUrl,
        timestamp: Date.now(),
        source: LOG_PREFIX,
      });
      return;
    }

    // Nothing styled yet, or everything already in the target material.
    applyRemaps(plan.remaps, woodSku, finishSku);
  }

  /**
   * Returns a gate to unanswered when the material swap cleared the categories
   * it governs, and reports any dependent selections that must be cleared too.
   *
   * Generic on purpose. It reads the same gate/selection dependency maps the
   * gate-card coordinator does, via the conductor so an admin rule override
   * cannot disagree with the constant -- there is no TFL, no "cabinets" and no
   * "knobs" named here. Any future gate whose categories a material swap can
   * invalidate gets this behaviour with no change to this function.
   *
   * The gate is CLEARED, not answered. Answering it 'none' would auto-apply the
   * "No Cabinet Doors" fallbacks and satisfy validateCartConfiguration, letting
   * the customer check out having never revisited the question. Clearing leaves
   * the required categories unapplied, so Add to Cart stays blocked until they
   * choose (Daniel, 2026-08-25: "it SHOULD gate the user").
   *
   * @param {Array<string>} clearedCategories
   * @returns {{extraUnapplies: Array<string>, clearedGateIds: Array<string>}}
   */
  function collectGateFallout(clearedCategories) {
    const extra = [];
    const clearedGateIds = [];
    const gateAnchors = {};
    const empty = { extraUnapplies: extra, clearedGateIds, gateAnchors };
    if (!clearedCategories || clearedCategories.length === 0) return empty;
    const conductor = window.MurphyDoorConfigurator.conductor;
    if (!conductor) return empty;
    const cleared = new Set(clearedCategories);

    try {
      const gateDeps =
        (conductor._getGateVisibilityDeps &&
          conductor._getGateVisibilityDeps()) ||
        CONSTANTS.CATEGORY_VISIBILITY_DEPS ||
        {};
      Object.keys(gateDeps).forEach((gateId) => {
        const config = gateDeps[gateId];
        const governed = (config && config.categories) || [];
        if (governed.length === 0) return; // swing/hinge govern nothing
        if (!governed.some((c) => cleared.has(c))) return;
        conductor._clearGateAnswer && conductor._clearGateAnswer(gateId);
        clearedGateIds.push(gateId);
        // Remember one category this gate governs. Leaving the summary needs a
        // tabId, and categoryToTabMap is keyed by real categories -- a gate is
        // synthetic and has no entry of its own.
        if (!gateAnchors[gateId]) gateAnchors[gateId] = governed[0];
      });
    } catch (error) {
      isDev() && console.error(LOG_PREFIX + ' gate clear failed:', error);
    }

    try {
      const selDeps =
        (conductor._getSelectionVisibilityDeps &&
          conductor._getSelectionVisibilityDeps()) ||
        CONSTANTS.SELECTION_VISIBILITY_DEPS ||
        {};
      const applied = conductor.getAppliedSelectionsMap
        ? conductor.getAppliedSelectionsMap()
        : null;
      Object.keys(selDeps).forEach((categoryId) => {
        const dep = selDeps[categoryId];
        if (!dep || !cleared.has(dep.dependsOnCategory)) return;
        // Only worth an unapply if something is actually applied there.
        const current = applied && applied.get ? applied.get(categoryId) : null;
        if (!current || current.variantId == null) return;
        extra.push(categoryId);
      });
    } catch (error) {
      isDev() && console.error(LOG_PREFIX + ' dependant sweep failed:', error);
    }

    return { extraUnapplies: extra, clearedGateIds, gateAnchors };
  }

  /**
   * Publishes a remap batch. Shared by the clean path (runRemap) and the
   * post-warning path (handleResetComponents) so both apply identically -- the
   * reset path used to skip this entirely, which is what stranded components.
   *
   * @param {Array} remaps
   * @param {string|null} woodSku
   * @param {string|null} finishSku
   */
  function applyRemaps(remaps, woodSku, finishSku) {
    if (!remaps || remaps.length === 0) return;

    isDev() &&
      console.log(
        LOG_PREFIX +
          ' remapping ' +
          remaps.length +
          ' selection(s) to ' +
          woodSku +
          '/' +
          finishSku,
        remaps.map((r) => r.categoryId)
      );

    // Raise the re-entrancy flag BEFORE publishing. Applying these selections
    // re-asserts the material preference derived from each variant, which
    // publishes MATERIAL_PREFERENCE_APPLIED again -- without this the module
    // would remap its own output forever (measured live: finish ping-ponging
    // UN -> MS -> UN). Cleared when the cascade reports completion, with a
    // timeout backstop so a cascade that never reports cannot latch the flag
    // on and silently disable remapping for the rest of the session.
    remapInFlight = true;
    if (inFlightTimer) clearTimeout(inFlightTimer);
    inFlightTimer = setTimeout(clearInFlight, IN_FLIGHT_MAX_MS);

    publish(CONSTANTS.EVENTS.BUSINESS_CASCADE_APPLY_GATE_DEFAULTS, {
      selections: remaps,
      context: 'material_remap_' + woodSku,
      timestamp: Date.now(),
      source: LOG_PREFIX,
    });
  }

  /**
   * Customer chose to keep their existing configuration. Reload the last
   * serialized URL -- url-param-parser.js re-applies it on boot, so this
   * restores the exact configuration they had before the material change,
   * including the old wood.
   */
  function handleKeepExisting() {
    const target = pendingRevertUrl;
    pendingRevertUrl = null;
    pendingResetCategories = [];
    pendingMaterial = null;
    // Declining the swap navigates away; a queued scroll would be meaningless
    // and could fire against the restored page.
    if (gateScrollTimer) {
      clearTimeout(gateScrollTimer);
      gateScrollTimer = null;
    }
    clearInFlight();
    if (!target) {
      // No history to return to (material changed as the very first action).
      // A plain reload lands on a clean configurator, which is the honest
      // outcome -- there is no prior configuration to preserve.
      isDev() &&
        console.warn(LOG_PREFIX + ' no revert URL recorded; reloading clean');
      // The swapped material is already applied and stamped for same-tab
      // resume; without this the clean reload would bring it straight back.
      window.MurphyDoorConfigurator.supportPersistenceManager?.clearSameTabResume?.();
      window.location.reload();
      return;
    }
    window.location.href = target;
  }

  /**
   * Customer accepted the reset and is keeping the new wood.
   *
   * Clears ONLY the styling components the wood change invalidates, one at a
   * time through the existing user-unapply flow -- the same event a card reset
   * publishes, so the conductor still clears state, re-persists the ?config=
   * URL and recalculates price for each. Deliberately NOT the full "Start
   * over" flow: that navigates to a bare URL and would take Width, Height and
   * the swing/hinge answers with it. Daniel: "if we can preserve measurements
   * that would be cool."
   *
   * Both halves of the plan are honoured: components that CAN move to the new
   * material are remapped, and only the ones that genuinely cannot are cleared.
   *
   * Every category here comes from planRemap, which never returns a
   * material-agnostic component, which is exactly why measurements survive
   * without needing to be named.
   */
  function handleResetComponents() {
    pendingRevertUrl = null;

    const material = pendingMaterial;
    pendingMaterial = null;
    let categories = pendingResetCategories.slice();
    pendingResetCategories = [];
    clearInFlight();

    // Re-plan against CURRENT state. "Keep the new wood and clear what can't
    // come" has to also mean "and carry across what can" -- honouring only the
    // unmappable half is what left components stranded on the old material.
    // Re-planning rather than replaying the stored plan means a selection the
    // customer changed while the warning was on screen is handled as it is now,
    // not as it was when the modal opened.
    let remaps = [];
    if (material) {
      const conductor = window.MurphyDoorConfigurator.conductor;
      try {
        const plan = planRemap(conductor, material.woodSku, material.finishSku);
        remaps = plan.remaps;
        categories = plan.unmappable.map((u) => u.categoryId);
      } catch (error) {
        // Keep the unapply list captured at warning time rather than silently
        // doing nothing -- clearing the conflicting components is the half the
        // customer explicitly asked for.
        isDev() &&
          console.error(LOG_PREFIX + ' reset re-plan failed:', error);
      }
    }

    // Suppress our own echo: each unapply re-asserts material preferences and
    // would otherwise re-enter the handler and re-raise the warning. Raised
    // before EITHER half runs, because both publish.
    remapInFlight = true;
    if (inFlightTimer) clearTimeout(inFlightTimer);
    inFlightTimer = setTimeout(clearInFlight, IN_FLIGHT_MAX_MS);

    // A cleared category can leave a gate answer and a dependent selection
    // describing components that are no longer there: the gate still reads
    // "Top and Bottom" and the knobs keep the customer's old choice with no
    // cabinet doors under them. Re-ask the gate and clear its dependants.
    const fallout = collectGateFallout(categories);
    const extraUnapplies = fallout.extraUnapplies;

    // ORDER MATTERS: clear first, remap LAST.
    //
    // image-layer-coordinator repaints on SELECTION_USER_UNAPPLY_COMPLETED as
    // well as on the cascade, so remapping first let the unapply's repaint land
    // afterwards and win with pre-remap layers -- the components had already
    // moved in state but the door still showed the old material until the
    // customer touched something else (reported live 2026-08-25). Ending on the
    // remap gives this path the same final event as the clean no-conflict path,
    // which has always rendered correctly.
    const toClear = categories.concat(extraUnapplies);
    if (toClear.length > 0) {
      isDev() &&
        console.log(
          LOG_PREFIX + ' resetting ' + toClear.length + ' component(s)',
          toClear
        );

      toClear.forEach((categoryId) => {
        publish(CONSTANTS.EVENTS.SELECTION_USER_UNAPPLY_REQUESTED, {
          categoryId,
          timestamp: Date.now(),
          source: LOG_PREFIX + '.materialChangeReset',
        });
      });
    }

    applyRemaps(
      remaps,
      material && material.woodSku,
      material && material.finishSku
    );

    // Send the customer to the question they now have to answer again.
    // Published LAST so it is the final navigation action: the remap cascade
    // above can auto-navigate to whatever it considers the next incomplete
    // step, which would otherwise leave them somewhere they cannot act -- the
    // gate's own categories are hidden while it is unanswered.
    //
    // UI_GATE_OPEN_REQUEST rather than a scroll of our own: navigation-manager
    // already handles gate cards (they carry data-category-gate, never
    // data-category, so they never go through updateCategoryDisplay) and
    // scrolls them into view exactly as a real category would.
    //
    // Deferred, not immediate. navigation-manager scrolls with
    // scrollIntoView, which resolves its target against the layout AT THE
    // MOMENT IT IS CALLED -- and this reset is the biggest reflow the
    // configurator ever does: the gate's categories are hidden and every
    // image is swapped for the new material. Measured live 2026-08-25: the
    // request fired, scrollIntoView ran, and the page never moved, because
    // the gate card's document position collapsed from ~3399px to ~396px
    // while the scroll was resolving.
    //
    // Same failure and same remedy as the ATC scroll at
    // md-config-ui-updater.js (Daniel, live-verified 2026-08-13: "calling
    // this immediately on the transition undershoots ... a second identical
    // call fired ~1s later, after nothing else changed, landed correctly").
    // Matching its 400ms so there is one settle delay in this codebase, not
    // two competing numbers.
    if (fallout.clearedGateIds.length > 0) {
      if (gateScrollTimer) clearTimeout(gateScrollTimer);
      gateScrollTimer = setTimeout(() => {
        gateScrollTimer = null;

        // LEAVE THE SUMMARY FIRST. Once the configuration is complete the
        // configurator swaps the options panel out for the summary +
        // Add-to-Cart view -- measured live 2026-08-25 in exactly this state:
        // #ConfiguratorOptions display:none, summary-content 1757px tall, and
        // the gate card sitting inside the hidden panel at height 0. Clearing
        // the gate hides its cabinet categories, so a customer who WANTS
        // cabinets had no way back to choose them: the question existed but
        // was unreachable, and the only escape was resetting the whole door.
        //
        // Same two-step the summary's own Edit link uses (updateSummaryList's
        // delegated handler): UI_TAB_OPEN_REQUEST to get back to the options
        // panel, then the open request on the next paint. Double rAF for the
        // same reason it gives -- without it the target is resolved against a
        // still-collapsed DOM and the expand is skipped.
        const conductor = window.MurphyDoorConfigurator.conductor;
        const ppd =
          conductor && conductor.getPerfectProductDictionary
            ? conductor.getPerfectProductDictionary()
            : null;
        const tabMap =
          ppd && ppd.progressMaps ? ppd.progressMaps.categoryToTabMap : null;

        fallout.clearedGateIds.forEach((gateId) => {
          const anchor = fallout.gateAnchors[gateId];
          const tabId =
            tabMap && anchor && tabMap.get ? tabMap.get(anchor) : null;
          if (tabId) {
            publish(CONSTANTS.EVENTS.UI_TAB_OPEN_REQUEST, {
              tabId,
              categoryId: anchor,
              reason: 'material_swap_invalidated_selection',
              timestamp: Date.now(),
              source: LOG_PREFIX + '.materialChangeReset',
            });
          }
          const openGate = () =>
            publish(CONSTANTS.EVENTS.UI_GATE_OPEN_REQUEST, {
              gateId,
              reason: 'material_swap_invalidated_selection',
              timestamp: Date.now(),
              source: LOG_PREFIX + '.materialChangeReset',
            });
          if (tabId && typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(() => requestAnimationFrame(openGate));
          } else {
            openGate();
          }
        });
      }, GATE_SCROLL_SETTLE_MS);
    }
  }

  function publish(eventName, payload) {
    if (!CoreEventBus || !eventName) return;
    try {
      CoreEventBus.publish(eventName, payload);
    } catch (error) {
      isDev() &&
        console.error(LOG_PREFIX + ' publish failed for ' + eventName, error);
    }
  }

  // ============================================================================
  // SUBSCRIPTIONS (MODULE LOAD TIME - RACE PROTECTION, Golden Pattern #9)
  // ============================================================================
  if (CoreEventBus) {
    const unsubBootstrap = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        bootstrapComplete = true;
        recordUrl();
        if (pendingCalls.length > 0) {
          pendingCalls.forEach((call) => {
            try {
              call.fn.apply(null, call.args);
            } catch (error) {
              isDev() &&
                console.error(LOG_PREFIX + ' queued call failed:', error);
            }
          });
          pendingCalls = [];
        }
      }
    );
    cleanupHandlers.push(unsubBootstrap);

    // The URL is written by persistence-manager during the apply, so read it
    // on the next frame -- reading synchronously here can capture the URL as
    // it was BEFORE updateURLState() ran, which would make the history lag one
    // step behind and hand back the wrong configuration on "stay".
    const unsubApplied = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SELECTION_APPLIED,
      () => {
        if (typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(recordUrl);
        } else {
          recordUrl();
        }
      }
    );
    cleanupHandlers.push(unsubApplied);

    const unsubMaterial = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.MATERIAL_PREFERENCE_APPLIED,
      handleMaterialPreferenceApplied
    );
    cleanupHandlers.push(unsubMaterial);

    // Lower the re-entrancy flag once our own bulk apply has finished
    // cascading. Same externally-observable signal
    // md-config-ui-gate-visibility-manager.js uses to defer its work around
    // cascades, rather than reaching into the cascade coordinator's state.
    [
      CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED,
      CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_FAILED,
    ].forEach((evt) => {
      if (!evt) return;
      cleanupHandlers.push(CoreEventBus.subscribe(evt, clearInFlight));
    });

    const unsubKeep = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.UI_MATERIAL_REMAP_KEEP_EXISTING,
      handleKeepExisting
    );
    cleanupHandlers.push(unsubKeep);

    const unsubFresh = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.UI_MATERIAL_REMAP_START_FRESH,
      handleResetComponents
    );
    cleanupHandlers.push(unsubFresh);
  }

  // Exposed for tests and for the conductor's teardown sweep.
  window.MurphyDoorConfigurator.materialRemapCoordinator = {
    // Read by utils.waitForConfiguratorIdle so a settle wait outlasts the
    // remap's debounce (one number, owned here).
    SETTLE_MS,
    handleMaterialPreferenceApplied,
    runRemap,
    _isRemapInFlight: () => remapInFlight,
    handleKeepExisting,
    handleResetComponents,
    planRemap,
    findVariantForMaterial,
    isMaterialAware,
    recordUrl,
    _getUrlHistory: () => urlHistory.slice(),
    destroy() {
      cleanupHandlers.forEach((fn) => {
        try {
          typeof fn === 'function' && fn();
        } catch (error) {
          isDev() && console.error(LOG_PREFIX + ' cleanup failed:', error);
        }
      });
      cleanupHandlers = [];
      pendingCalls = [];
    },
  };
})(window);
